import { BasicContainer, FlexCol, FlexRow, H1Text } from '@minecraft.atoms';
import React, { FC, useMemo } from 'react';
import { useTranslation } from '@blocs.i18n';
import { gqlAccount } from '@minecraft.graphql-types';
import { CheckoutHead } from './CheckoutHead';
import { PlanType } from '../usePlans';
import { TestimonialCard } from '../PlanSelection/TestimonialCard';
import { PlanCode } from '../enums/plans';
import { CheckoutProps } from './types';
import { PlanFeatureCard } from './PlanFeatureCard';
import { FeatureList } from '../PlanSelection/FeatureList';
import { PlanExplorer } from './PlanExplorer';

type DesktopRegistrationUpsellProps = {
  setIsAnnual: (isAnnual: boolean) => void;
  isAnnual: boolean;
  selectedPlan?: PlanType;
  plans: PlanType[];
  countryCode: gqlAccount['country']['code'];
  currencyCode: string;
  isToggleLocked?: boolean;
  disableTrial?: boolean;
  onCompletePurchase: CheckoutProps['onCompletePurchase'];
  onPaymentCompleteRedirect: CheckoutProps['onPaymentCompleteRedirect'];
  setSelectedPlan: CheckoutProps['setSelectedPlan'];
};

export const DesktopRegistrationUpsell: FC<DesktopRegistrationUpsellProps> = React.memo(
  ({
    setIsAnnual,
    isAnnual,
    plans,
    countryCode,
    currencyCode,
    isToggleLocked,
    selectedPlan,
    setSelectedPlan,
    disableTrial,
  }) => {
    const { t } = useTranslation();

    const orderedHighlightedFeatures = useMemo(() => {
      if (!selectedPlan) return [];

      const featuresToHighlight = new Map([
        ['free-profile', 1],
        ['browse-role', 2],
        ['media-request', 3],
        ['saved-searches', 4],
        ['createReals', 5],
        ['role-tracker', 6],
      ]);

      const keyFeatureSet = new Set(selectedPlan.keyFeatures.map((f) => f.code));

      return selectedPlan.features
        .filter((feature) => featuresToHighlight.has(feature.code) && !keyFeatureSet.has(feature.code))
        .sort((a, b) => (featuresToHighlight.get(a.code) ?? 0) - (featuresToHighlight.get(b.code) ?? 0));
    }, [selectedPlan]);

    return (
      <FlexCol justifyContent="start" alignItems="center" w="full" gap="6" maxW="2xl" mx="auto">
        <CheckoutHead
          isAnnual={isAnnual}
          setIsAnnual={setIsAnnual}
          isToggleLocked={isToggleLocked}
          plans={plans}
          countryCode={countryCode}
          currencyCode={currencyCode}
        />
        <FlexCol w="full" gap="4" alignItems="center">
          <BasicContainer w="full" mt="4" mx="auto">
            <FlexCol w="full" gap="4" justifyContent="center" alignItems="center">
              <FlexRow gap="2" maxW="md">
                <H1Text fontWeight="semibold" lineHeight="expanded" align="center">
                  {t('common:paywall.plan.upsell.title')}
                </H1Text>
              </FlexRow>
              <FlexCol gap="8" w="full" justifyContent="center" alignItems="center" m="auto" mt="4">
                {plans.map((plan) => {
                  const isFreePlan = plan?.code === PlanCode.Free;
                  const isPremiumPlan = plan?.code === PlanCode.Premium;
                  const isSelectedPlan = selectedPlan?.billingCode === plan?.billingCode;
                  if (!isPremiumPlan && !isFreePlan) return null;
                  return (
                    <PlanFeatureCard
                      key={plan?.billingCode}
                      plan={plan}
                      billed={plan?.billed}
                      price={Number(plan?.price)}
                      title={isFreePlan ? plan.title : t('common:membership')}
                      billingCode={plan?.billingCode}
                      temperature={plan?.temperature}
                      countryCode={countryCode}
                      disableTrial={disableTrial}
                      isFreePlan={isFreePlan}
                      setSelectedPlan={setSelectedPlan}
                      isSelectedPlan={isSelectedPlan}
                    >
                      <FlexCol w="full" h="full" className="cn_atom_px-2" justifyContent="flex-start" gap="2" mt="4">
                        <FeatureList
                          iconName={plan.iconName || 'check-circle'}
                          features={plan.keyFeatures}
                          temperature={plan?.temperature}
                        />
                      </FlexCol>
                    </PlanFeatureCard>
                  );
                })}
                <PlanExplorer
                  selectedPlan={selectedPlan}
                  customFeatures={orderedHighlightedFeatures}
                  ctaText={t('common:paywall.plan.features.exploreAdditionalFeatures')}
                />
              </FlexCol>
            </FlexCol>
          </BasicContainer>
          <BasicContainer w="full" mt="8">
            <TestimonialCard
              fullWidth
              bgColor="warmPaywallLightBg"
              borderColor="warmPaywall"
              bgOpacity={0.5}
              author="Jasmine Cowan"
              message={t('common:paywall.testimonial.message.jasmine')}
              image="jasmine.png"
              memberSince="2018"
            />
          </BasicContainer>
        </FlexCol>
      </FlexCol>
    );
  }
);
